import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PreOrderCountDetailsDto } from '@kotipizzagroup/kotipizza-orders-api-client';
import { alpha, Theme } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { FinnishDayAndMonthFormat } from '../../utils/dateHelpers';

type Props = {
  date: Dayjs;
  onDateChange: (date: Dayjs) => void;
  preorders?: PreOrderCountDetailsDto[];
};

const DESKTOP_BREAKPOINT = 'md' as const;

// Days either side of *today*, not of the selection: the row is a fixed calendar the highlight moves
// along. Recomputing the set from the selection would slide it under the finger on a phone, and on
// desktop would leave nothing stable to glide between — the centring below scrolls the same buttons
// rather than swapping them. Biased forward because that is where preorders live.
const DAY_RANGE = { past: 7, future: 14 };

// The desktop row shows a fixed window onto that range. Nine is odd on purpose: it leaves four whole
// days either side of the selection, so a centred day lands on a whole multiple of the chip pitch and
// the edges stay crisp instead of cutting two chips in half.
const DESKTOP_CHIP = 88;
const DESKTOP_GAP = 8;
const DESKTOP_VISIBLE_DAYS = 9;
const DESKTOP_WINDOW = DESKTOP_VISIBLE_DAYS * DESKTOP_CHIP + (DESKTOP_VISIBLE_DAYS - 1) * DESKTOP_GAP;

type DayState = 'selected' | 'today' | 'past' | 'future';

/** Whether a day falls inside the row a given anchor produces. Both are start-of-day timestamps. */
const isWithinRange = (day: number, anchor: number): boolean => {
  const offset = dayjs(day).diff(dayjs(anchor), 'day');

  return offset >= -DAY_RANGE.past && offset <= DAY_RANGE.future;
};

/**
 * The day picker for the orders list: one row of narrow vertical buttons, scrolled sideways. It replaces
 * a 3×3 grid that cost three rows on a phone — and that recomputed its own window from the selected day,
 * so every tap slid it under the finger.
 */
export const OrdersDateStrip: React.FC<Props> = ({ date, onDateChange, preorders }) => {
  const { t } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  const hasCentredRef = useRef(false);

  const today = dayjs().startOf('day');
  const startOfToday = today.valueOf();
  const startOfSelected = date.startOf('day').valueOf();

  /**
   * The preorder counts keyed by the start of their day, so the row below reads each day's badge in
   * one lookup. Scanning the list per day meant parsing every preorder's date up to 22 times over.
   */
  const preorderCountByDay = useMemo(() => {
    // The count is left as it came, undefined included: whether a day *has* an entry is the question
    // the badge is gated on, and `has` answers it without turning a missing count into a zero.
    const byDay = new Map<number, number | undefined>();

    preorders?.forEach((preorder) => {
      byDay.set(dayjs(preorder.date).startOf('day').valueOf(), preorder.pizzaProductCount);
    });

    return byDay;
  }, [preorders]);

  /**
   * Anchored on today, except when a day the strip cannot reach is selected — the calendar, or a
   * link straight to a date months out — and then it re-anchors on that day so the selection is
   * never highlighted off-screen.
   *
   * Carried in a ref rather than recomputed from the selection, because "can the strip reach it" has
   * to be asked of the row *currently on screen*, not of today. Measured against today instead, a
   * selection far from today re-anchored on every tap, so tapping the chip beside the selected one
   * shifted all 22 buttons by a day — the row sliding under the finger that the grid layout was
   * replaced to stop. Now the row moves only when the selection actually leaves it.
   */
  const anchorRef = useRef(isWithinRange(startOfSelected, startOfToday) ? startOfToday : startOfSelected);
  if (!isWithinRange(startOfSelected, anchorRef.current)) {
    anchorRef.current = startOfSelected;
  }
  const anchor = anchorRef.current;

  const days = useMemo(() => {
    const first = dayjs(anchor).subtract(DAY_RANGE.past, 'day');

    return Array.from({ length: DAY_RANGE.past + DAY_RANGE.future + 1 }, (_, index) => first.add(index, 'day'));
  }, [anchor]);

  /**
   * Bring the selected day to the middle of the row. This is the whole of the strip's motion, on both
   * layouts: the phone scrolls its 22 days by hand and this glides the tapped one back to the centre,
   * while the desktop window shows nine of the same days and cannot be scrolled by hand at all, so a
   * click is the only thing that ever moves it. `scrollTo` works either way — a container with
   * `overflow: hidden` is still a scroll container, it just refuses the user.
   *
   * Measured from bounding rects rather than `offsetLeft`: the buttons' offset parent is whatever
   * positioned ancestor the page happens to provide, not the scroller, so `offsetLeft` carried the
   * strip's own page offset and pushed the selection left of centre. Days within half a window of
   * either end of the range still land off-centre — there is nothing left to scroll past them.
   */
  const centreSelected = useCallback((behavior: ScrollBehavior) => {
    const scroller = scrollerRef.current;
    const selected = selectedRef.current;
    if (!scroller || !selected) return;

    const scrollerBox = scroller.getBoundingClientRect();
    const selectedBox = selected.getBoundingClientRect();
    const offsetWithinScroller = selectedBox.left - scrollerBox.left + scroller.scrollLeft;
    const left = offsetWithinScroller - (scroller.clientWidth - selectedBox.width) / 2;

    scroller.scrollTo({ left, behavior });
  }, []);

  // Glide on every change of day, but land instantly on the first paint: an opening animation would
  // read as the strip moving on its own. Keyed on the day itself rather than the `date` object,
  // because the selection comes from the URL and every location change hands down a fresh instance:
  // depending on the object would re-centre the row whenever an order panel opened or a filter
  // moved — a glide with no day change behind it, and on a phone a snap back mid-drag.
  useEffect(() => {
    centreSelected(hasCentredRef.current ? 'smooth' : 'auto');
    hasCentredRef.current = true;
  }, [startOfSelected, centreSelected]);

  /**
   * The centre moves whenever the row's width does — crossing the desktop breakpoint, collapsing the
   * side drawer, plain window resizing — and none of those change `date`, so they would otherwise leave
   * the selection sitting off to one side. Instantly rather than smoothly: dragging a window edge should
   * not set a glide chasing the pointer.
   */
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const observer = new ResizeObserver(() => centreSelected('auto'));
    observer.observe(scroller);

    return () => observer.disconnect();
  }, [centreSelected]);

  const stateOf = (day: Dayjs): DayState => {
    if (day.isSame(date, 'day')) return 'selected';
    if (day.isSame(today, 'day')) return 'today';

    return day.isBefore(today, 'day') ? 'past' : 'future';
  };

  return (
    <Strip>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fi">
        <CalendarButton type="button" onClick={() => setCalendarOpen(true)} aria-label={t('orderList.calendar')}>
          <CalendarMonth fontSize="small" />
        </CalendarButton>
        <MobileDatePicker
          open={calendarOpen}
          onClose={() => setCalendarOpen(false)}
          value={date}
          onChange={(newValue) => newValue && onDateChange(dayjs(newValue))}
          slotProps={{ textField: { sx: { display: 'none' } } }}
        />
      </LocalizationProvider>

      <Scroller ref={scrollerRef}>
        {days.map((day) => {
          const state = stateOf(day);
          // Normalised on both sides of the lookup, so a DST shift in the generated row cannot put a
          // day a millisecond off its own key.
          const dayKey = day.startOf('day').valueOf();
          const hasPreorders = preorderCountByDay.has(dayKey);

          return (
            <DayButton
              key={day.valueOf()}
              type="button"
              ref={state === 'selected' ? selectedRef : undefined}
              $state={state}
              aria-pressed={state === 'selected'}
              onClick={() => onDateChange(day)}
            >
              <Weekday>{day.format('ddd')}</Weekday>
              <DayDate>{day.format(FinnishDayAndMonthFormat)}</DayDate>
              {hasPreorders && !day.isBefore(today, 'day') ? (
                <PreorderCount>{preorderCountByDay.get(dayKey)}</PreorderCount>
              ) : null}
            </DayButton>
          );
        })}
      </Scroller>
    </Strip>
  );
};

const Strip = styled.div`
  display: flex;
  align-items: stretch;
  gap: 6px;
  width: 100%;
  padding-top: 12px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  ${({ theme }) => theme.breakpoints.up(DESKTOP_BREAKPOINT)} {
    gap: ${DESKTOP_GAP}px;
  }
`;

const buttonBase = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border: 0;
  border-radius: 8px;
  font: inherit;
  text-transform: uppercase;
  text-align: center;
  cursor: pointer;
  user-select: none;

  /* Inset, so the scroller cannot clip it: setting 'overflow-x' turns 'overflow-y' to 'auto' as well,
     and an outline drawn outwards loses its top and bottom edges to that. */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: -2px;
  }
`;

// Outside the scroller, so it stays reachable however far the days are scrolled.
const CalendarButton = styled.button`
  ${buttonBase}
  flex: 0 0 44px;
  min-height: 56px;
  transition: background-color 150ms ease;
  background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.2)};
  color: ${({ theme }) => theme.palette.primary.main};

  &:hover {
    background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.3)};
  }

  ${({ theme }) => theme.breakpoints.up(DESKTOP_BREAKPOINT)} {
    flex: 0 0 60px;
    min-height: 76px;

    svg {
      font-size: 24px;
    }
  }
`;

// The repo otherwise wraps or reflows rather than scrolling sideways; this strip is the exception. The
// scrollbar is hidden — it would sit under the buttons as a permanent grey line — leaving the partly
// visible day at each edge as the hint that the row continues. No scroll snapping either: the centring
// effect decides where the strip lands, and snap points fight the smooth scroll for the last few pixels.
//
// Desktop goes further and takes the scrolling away entirely: `hidden` rather than `auto` still lets the
// centring effect drive it, but the row answers to clicks alone, and the window is capped so the nine
// visible days sit on whole chip boundaries. Below the width that cap needs, it simply shows fewer days
// and cuts the edge ones, the same way the phone does.
const Scroller = styled.div`
  display: flex;
  flex: 1;
  gap: 6px;
  min-width: 0;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.up(DESKTOP_BREAKPOINT)} {
    gap: ${DESKTOP_GAP}px;
    max-width: ${DESKTOP_WINDOW}px;
    overflow-x: hidden;
  }
`;

// Text on the tinted chips is primary.dark: primary.main on them falls short of 4.5:1.
const dayColors = (theme: Theme): Record<DayState, { fill: string; hover: string; text: string }> => {
  const idle = {
    fill: theme.palette.neutral.fill,
    hover: theme.palette.neutral.fillHover,
    text: theme.palette.primary.dark,
  };
  return {
    selected: {
      fill: theme.palette.primary.main,
      hover: theme.palette.primary.dark,
      text: theme.palette.primary.contrastText,
    },
    past: {
      fill: alpha(theme.palette.primary.main, 0.2),
      hover: alpha(theme.palette.primary.main, 0.3),
      text: theme.palette.primary.dark,
    },
    today: idle,
    future: idle,
  };
};

const DayButton = styled.button<{ $state: DayState }>`
  ${buttonBase}
  position: relative;
  flex: 0 0 56px;
  min-height: 56px;
  transition: background-color 150ms ease, color 150ms ease, box-shadow 150ms ease;
  background-color: ${({ theme, $state }) => dayColors(theme)[$state].fill};
  color: ${({ theme, $state }) => dayColors(theme)[$state].text};
  box-shadow: ${({ theme, $state }) => ($state === 'today' ? `inset 0 0 0 2px ${theme.palette.primary.main}` : 'none')};

  /* Hover darkens the fill instead of ringing the button: a ring drawn outwards is clipped by the
     scroller, and on *today* a green one would land straight on the green ring already there. */
  &:hover {
    background-color: ${({ theme, $state }) => dayColors(theme)[$state].hover};
  }

  ${({ theme }) => theme.breakpoints.up(DESKTOP_BREAKPOINT)} {
    flex: 0 0 ${DESKTOP_CHIP}px;
    min-height: 76px;
  }
`;

const Weekday = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  opacity: 0.8;

  ${({ theme }) => theme.breakpoints.up(DESKTOP_BREAKPOINT)} {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const DayDate = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 600;

  ${({ theme }) => theme.breakpoints.up(DESKTOP_BREAKPOINT)} {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

// Inside the cell's bounds: the scroller clips anything hanging over its edge.
const PreorderCount = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  min-width: 18px;
  padding: 0 4px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.palette.highlight.dark};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: bold;
  color: ${({ theme }) => theme.palette.common.white};

  ${({ theme }) => theme.breakpoints.up(DESKTOP_BREAKPOINT)} {
    top: 4px;
    right: 4px;
    height: 22px;
    min-width: 22px;
  }
`;
